import { Children, cloneElement, useContext } from 'react';

import { useAgenciesGetQuery } from '../../Common/Queries/agenciesQueries';
import { useDepartmentGetQuery } from '../../Common/Queries/departmentQueries';
import { useFiltersGetQuery } from '../../Common/Queries/filterQueries';
import { useLevelsGetQuery } from '../../Common/Queries/levelQueries';
import { usePanelResourceGetQuery } from '../../Common/Queries/panelQueries';
import { usePositionsGetQuery } from '../../Common/Queries/positionsQueries';
import { useSkillsGetQuery } from '../../Common/Queries/skillQueries';
import { useStreamGetQuery } from '../../Common/Queries/streamQueries';
import { useUsersGetQuery } from '../../Common/Queries/usersQueries';

import { isLogin } from '../../utils';

import { ALL_DATA_LIMIT, AuthContext, DURATION_TIME, ROLES } from '../../constants';

export function GetFilterDataContainer({ children, entity, filter, ...props }) {
  const { userInfo } = useContext(AuthContext);
  const role = userInfo?.roles?.[0];

  const filterData = useFiltersGetQuery({ entity, field: filter }, { enabled: filter === 'type' });

  const streamList = useStreamGetQuery(
    { limit: ALL_DATA_LIMIT },
    {
      enabled: isLogin() && (filter === 'stream' || filter === 'positionTitle'),
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const statusList = useFiltersGetQuery(
    { entity: 'application', field: 'status' },
    {
      enabled:
        isLogin() &&
        filter === 'status' &&
        (entity === 'application' || entity === 'referral' || entity === 'Events'),
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  // distinct specific application statuses (PRE-SCREENING, INTERVIEW SCHEDULED, ...) for
  // the Events and application tables' Application Status column filter
  const detailedStatusList = useFiltersGetQuery(
    { entity: 'application', field: 'detailedStatus' },
    {
      enabled:
        isLogin() &&
        filter === 'applicationStatus' &&
        (entity === 'Events' || entity === 'application'),
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const departmentList = useDepartmentGetQuery(
    { limit: ALL_DATA_LIMIT },
    {
      enabled: isLogin() && role !== ROLES.AGENCY && filter === 'department',
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const skillList = useSkillsGetQuery(
    { limit: ALL_DATA_LIMIT },
    {
      enabled: isLogin() && filter === 'skill',
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const levelList = useLevelsGetQuery(
    { limit: ALL_DATA_LIMIT },
    {
      enabled: isLogin() && filter === 'level',
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const positionList = usePositionsGetQuery(
    { limit: ALL_DATA_LIMIT },
    {
      enabled: isLogin() && (filter === 'positionTitle' || filter === 'source'),
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const panelResourceList = usePanelResourceGetQuery(
    { limit: ALL_DATA_LIMIT },
    {
      enabled: isLogin() && entity !== 'Events' && filter === 'panelID',
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const eventPanelList = useUsersGetQuery(
    { limit: ALL_DATA_LIMIT },
    {
      enabled: isLogin() && entity === 'Events' && filter === 'panelID',
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const recruiterList = useUsersGetQuery(
    {
      limit: ALL_DATA_LIMIT,
      role: [
        ROLES.ADMIN,
        ROLES.HR,
        ROLES.HIRING_MANAGER,
        ROLES.SUPER_ADMIN,
        ROLES.TENANT_OWNER,
      ].join(','),
    },
    {
      enabled: isLogin() && role !== ROLES.AGENCY && filter === 'recruiterID',
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  const agencyList = useAgenciesGetQuery(
    { limit: ALL_DATA_LIMIT },
    {
      enabled: isLogin() && role !== ROLES.AGENCY && filter === 'agencyIDs',
      refetchInterval: DURATION_TIME,
      refetchIntervalInBackground: true,
    }
  );

  return Children.only(
    cloneElement(children, {
      filterData,
      entity,
      filter,
      streamList,
      statusList,
      detailedStatusList,
      departmentList,
      skillList,
      levelList,
      positionList,
      panelList: entity === 'Events' ? eventPanelList : panelResourceList,
      recruiterList,
      agencyList,
      ...props,
    })
  );
}
